import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Loader, Paragraph, Select } from '@components/atoms'
import { CardCourse } from '@components/atoms/CardCourse'
import Pagination from '@components/atoms/Pagination'
import SearchInput from '@components/atoms/SearchInput'
import { DocumentFolder16Regular, Heart16Regular } from '@fluentui/react-icons'
import { useInputBounce, useQuery } from '@hooks'
import useFilters from '@hooks/useFilters'
import { AllClientModulesResponse } from '@models/course.model'
import { coursesAPI } from '@services/CoursesAPI'
import { dashboardAPI } from '@services/DashboardAPI'

import { CoursesStyles } from './style'

const {
	container,
	titleIndex,
	myCourses,
	myOtherCourses,
	TabContainer,
	TabItem,
	searchContainer,
	NotFound,
	showMyCoursesButton,
	title,
	lastAccessContainer,
	paginationContainer,
	myCoursesTitle,
} = CoursesStyles

type TabType = 'all' | 'favorites'
type Option = {
	value: 'alphabetical' | 'lastAccess'
	option: string
}
export const Courses = () => {
	const query = useQuery()
	const navigate = useNavigate()

	const pageQuery = Number(query.get('page')) || 1
	const searchQs = query.get('search') || ''

	const [search, setSearch] = useState(decodeURIComponent(searchQs))
	const [searchQuery, setSearchQuery] = useState(decodeURIComponent(searchQs))
	const [page, setPage] = useState(pageQuery)

	const selectOptions: Option[] = [
		{ value: 'alphabetical', option: 'Ordem alfabética' },
		{ value: 'lastAccess', option: 'Ordem de acesso' },
	]
	const [isFavoriteState, setIsFavorite] = useState<{ [key: string]: boolean }>(
		{}
	)
	const [dataModulesLocale, setDataModulesLocale] =
		useState<AllClientModulesResponse>()

	const [select, setSelect] = useState<Option | undefined>(selectOptions[0])
	const [activeTab, setActiveTab] = useState<TabType>('all')
	const [getAllClientModules, { data: dataModules, isSuccess, isLoading }] =
		coursesAPI.useGetAllClientModulesMutation()
	const { data } = dashboardAPI.useListMeQuery()

	const [getLastsAccessedClass] = coursesAPI.useGetLastsAccessedClassMutation()

	const [createFavoriteClass] = coursesAPI.useCreateFavoriteClassMutation()

	useEffect(() => {
		if (isSuccess && dataModules) {
			const favoritesFromBackend = dataModules.modules.reduce<
				Record<number, boolean>
			>((acc, module) => {
				acc[module.id] = module.isFavorite

				return acc
			}, {})

			setIsFavorite(favoritesFromBackend)
		}
	}, [dataModules, isSuccess])

	useEffect(() => {
		getAllClientModules({
			page,
			search: encodeURIComponent(searchQuery),
		}).then((res) => {
			if ('data' in res) {
				setDataModulesLocale(res.data)
			}
		})

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, searchQuery])

	const handleAccess = (id: number) => {
		getLastsAccessedClass(id).then(() => navigate(`/cursos/meus-cursos/${id}`))
	}
	const handleFavorite = useCallback(
		async (id: number) => {
			const isFavoriteNow = !dataModulesLocale?.favoriteClasses.find(
				(module) => module.id === id
			)?.id

			try {
				await createFavoriteClass({ moduleId: id, isFavorite: isFavoriteNow })

				setIsFavorite((prevState) => ({
					...prevState,
					[id]: isFavoriteNow,
				}))
				setDataModulesLocale((prevState) => {
					if (!prevState) return prevState

					const updatedModules = prevState.modules.map((module) =>
						module.id === id ? { ...module, isFavorite: isFavoriteNow } : module
					)

					const updatedModule = updatedModules.find(
						(module) => module.id === id
					)

					// eslint-disable-next-line no-nested-ternary
					const updatedFavoriteClasses = isFavoriteNow
						? updatedModule
							? [
									...prevState.favoriteClasses.filter(
										(favModule) => favModule.id !== id
									),
									updatedModule,
							  ]
							: prevState.favoriteClasses
						: prevState.favoriteClasses.filter((module) => module.id !== id)

					return {
						...prevState,
						modules: updatedModules,
						favoriteClasses: updatedFavoriteClasses,
					}
				})
			} catch (error) {
				//
			}
		},
		[dataModulesLocale, createFavoriteClass]
	)

	const tabOption = [
		{
			id: 'all',
			text: 'Todos',
			IconComponent: DocumentFolder16Regular,
		},
		{
			id: 'favorites',
			text: 'Favoritos',
			IconComponent: Heart16Regular,
		},
	]

	const filteredModules =
		dataModules &&
		dataModules.modules &&
		dataModules.modules
			.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
			.sort((a, b) => {
				if (select?.value === 'alphabetical') {
					return a.name.localeCompare(b.name)
				}
				if (select?.value === 'lastAccess') {
					if (a.lastAcess === null && b.lastAcess === null) return 0
					if (a.lastAcess === null) return 1
					if (b.lastAcess === null) return -1

					return b.lastAcess.localeCompare(a.lastAcess)
				}

				return 0
			})

	const searchCallback = useCallback(
		(value: string) => {
			navigate('/cursos?page=1')
			setSearchQuery(value)
		},
		[navigate]
	)

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	useInputBounce(search, searchCallback)

	useFilters({
		search: searchQuery,
		fallbackUrl: '/cursos',
	})

	return (
		<div className={container()}>
			<div className={titleIndex()}>
				<h1 className={title()}>
					{activeTab === 'all'
						? 'Últimos cursos acessados'
						: 'Cursos mais indicados para o seu perfil '}
				</h1>
			</div>
			{isLoading ? (
				<Loader />
			) : (
				<div>
					{activeTab === 'all' ? (
						<div className={lastAccessContainer()}>
							{isSuccess &&
							dataModules &&
							dataModules.lastAccessModules.length > 0 ? (
								<div className={myCourses()}>
									{dataModules.lastAccessModules.map(
										({ name, id, image, lastAcess }, index) => (
											<CardCourse
												onFavorite={() => handleFavorite(id)}
												isFavorite={!!isFavoriteState[id]}
												onClick={() => handleAccess(id)}
												name={data?.name || ''}
												image={image || ''}
												title={name}
												key={index}
												date={lastAcess}
												profilePicture={data?.imageUrl || ''}
											/>
										)
									)}
								</div>
							) : (
								<NotFound>
									<DocumentFolder16Regular />
									<Paragraph
										css={{
											color: '$neutral90',
											fontSize: '16px',
											textAlign: 'center',
										}}
									>
										Acesse um de seus cursos abaixo e consiga sua aprovação no
										concurso dos sonhos!
									</Paragraph>
								</NotFound>
							)}
						</div>
					) : (
						isSuccess &&
						dataModules &&
						dataModules?.others.length > 0 && (
							<div style={{ marginBottom: '7rem' }}>
								<div className={myOtherCourses()}>
									{dataModules.others.map(({ name, image, url }, index) => (
										<CardCourse
											block
											image={image || ''}
											title={name}
											url={url}
											key={index}
										/>
									))}
								</div>
							</div>
						)
					)}
				</div>
			)}
			<div>
				<div className={myCoursesTitle()}>
					<h1 className={title()}>Meus Cursos</h1>
				</div>

				<TabContainer>
					{tabOption.map((tab, index) => (
						<TabItem
							key={tab.id}
							className={activeTab === tab.id ? 'active' : ''}
							onClick={() => setActiveTab(tab.id as TabType)}
							style={{ paddingLeft: index === 0 ? 12 : 6 }}
						>
							<tab.IconComponent
								className={activeTab === tab.id ? 'active' : ''}
							/>
							<Paragraph
								paragraphType='p1'
								className={activeTab === tab.id ? 'active' : ''}
								css={{
									color: '$neutral70',
									fontSize: '16px',
									'&.active': {
										color: '$blue20',
										fontWeight: '$bold',
									},
								}}
							>
								{tab.text}
							</Paragraph>
						</TabItem>
					))}
				</TabContainer>

				{activeTab === 'all' && (
					<div className={searchContainer()}>
						<SearchInput
							placeholder='Busque pelo nome'
							maxLength={30}
							onChange={(e) => setSearch(e.target.value)}
							value={search}
							css={{ maxWidth: '100%', '@md': { maxWidth: '287px' } }}
						/>

						<Select
							placeholder='Ordem alfabética'
							onChange={(e) => {
								setSelect(e as Option)
							}}
							selectedValue={select}
							options={selectOptions}
							css={{ maxWidth: '100%', '@md': { maxWidth: '287px' } }}
						/>
					</div>
				)}
				{isLoading ? (
					<Loader />
				) : (
					<div>
						{activeTab === 'favorites' ? (
							<div>
								{isSuccess &&
								dataModulesLocale &&
								dataModulesLocale.favoriteClasses.length > 0 ? (
									<div className={myCourses()}>
										{dataModulesLocale.favoriteClasses.map(
											({ name, image, lastAcess, id }, index) => (
												<CardCourse
													onFavorite={() => handleFavorite(id)}
													isFavorite
													onClick={() => handleAccess(id)}
													profilePicture={data?.imageUrl || ''}
													name={data?.name || ''}
													image={image || ''}
													title={name}
													key={index}
													date={lastAcess}
												/>
											)
										)}
									</div>
								) : (
									<NotFound className='favorite'>
										<Heart16Regular />
										<Paragraph
											css={{
												color: '$neutral90',
												fontSize: '16px',
												textAlign: 'center',
											}}
										>
											Você ainda não favoritou nenhum curso.
											<br />
											Favorite um de seus cursos e facilite seus estudos.
										</Paragraph>
										<button
											type='button'
											className={showMyCoursesButton()}
											onClick={() => setActiveTab('all')}
										>
											Ver meus cursos
										</button>
									</NotFound>
								)}
							</div>
						) : (
							<div className={myCourses()}>
								{isSuccess &&
									filteredModules &&
									filteredModules.length > 0 &&
									filteredModules.map(
										({ id, name, lastAcess, image }, index) => (
											<CardCourse
												onFavorite={() => handleFavorite(id)}
												isFavorite={!!isFavoriteState[id]}
												onClick={() => handleAccess(id)}
												name={data?.name || ''}
												image={image || ''}
												title={name}
												key={index}
												date={lastAcess}
												profilePicture={data?.imageUrl || ''}
											/>
										)
									)}
							</div>
						)}
					</div>
				)}
			</div>
			{activeTab === 'all' && (
				<div className={paginationContainer()}>
					{dataModules && dataModules?.meta && (
						<Pagination pageCount={dataModules?.meta.lastPage || 0} />
					)}
				</div>
			)}
		</div>
	)
}
